/**
 * DirectionBuilder - Direction Entity 생성을 위한 Builder
 */
import type { ICanvasEngineEntityController } from '../../../types/interface'
import type { Vector2d, CanvasEngineExportEntityType } from '../../../types'
import { SHAPES } from '../../../const'
import type { DirectionInitConfig } from './directionEntity'

/**
 * DirectionBuilder 클래스
 */
export class DirectionBuilder {
  private canvasEngine: ICanvasEngineEntityController
  private config: Partial<DirectionInitConfig> = {}

  /**
   * DirectionBuilder 생성자
   * @param canvasEngine - CanvasEngine 컨트롤러
   */
  constructor(canvasEngine: ICanvasEngineEntityController) {
    this.canvasEngine = canvasEngine
    this.reset()
  }

  /**
   * 설정 초기화
   * @returns this
   */
  reset(): DirectionBuilder {
    this.config = {
      interactionMode: true,
    }
    return this
  }

  /**
   * ID 설정
   * @param id - Entity ID
   * @returns this
   */
  setId(id: string): DirectionBuilder {
    this.config.id = id
    return this
  }

  /**
   * 생성 위치 설정
   * @param point - 위치 좌표
   * @returns this
   */
  setCreatePoint(point: Vector2d): DirectionBuilder {
    this.config.createPoint = point
    return this
  }

  /**
   * Balloon 위치 설정
   * @param point - Balloon 위치 좌표
   * @returns this
   */
  setBalloonPoint(point: Vector2d): DirectionBuilder {
    this.config.balloonPoint = point
    return this
  }

  /**
   * 색상 설정
   * @param color - 색상
   * @returns this
   */
  setColor(color: string): DirectionBuilder {
    if (!this.config.config) {
      this.config.config = { color }
    } else {
      this.config.config.color = color
    }
    return this
  }

  /**
   * Points 설정
   * @param points - 점 배열
   * @returns this
   */
  setPoints(points: number[]): DirectionBuilder {
    this.config.points = points
    return this
  }

  /**
   * 레이어 ID 설정
   * @param layerId - 레이어 ID
   * @returns this
   */
  setLayerId(layerId: string): DirectionBuilder {
    this.config.layerId = layerId
    return this
  }

  /**
   * Balloon 글자색 설정
   * @param color - 색상
   * @returns this
   */
  setBalloonFontColor(color: string): DirectionBuilder {
    this.config.balloonFontColor = color
    return this
  }

  /**
   * Balloon 배경색 설정
   * @param color - 색상
   * @returns this
   */
  setBalloonFillColor(color: string): DirectionBuilder {
    this.config.balloonFillColor = color
    return this
  }

  /**
   * Balloon 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setBalloonScale(scale: number): DirectionBuilder {
    this.config.balloonScale = scale
    return this
  }

  /**
   * 연결선 두께 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setConnectedLineThicknessScale(scale: number): DirectionBuilder {
    this.config.connectedLineThicknessScale = scale
    return this
  }

  /**
   * Balloon 글자 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setBalloonFontScale(scale: number): DirectionBuilder {
    this.config.balloonFontScale = scale
    return this
  }

  /**
   * Point 크기 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setPointShapeScale(scale: number): DirectionBuilder {
    this.config.pointShapeScale = scale
    return this
  }

  /**
   * 영역 테두리 두께 스케일 설정
   * @param scale - 스케일
   * @returns this
   */
  setAreaBorderThicknessScale(scale: number): DirectionBuilder {
    this.config.areaBorderThicknessScale = scale
    return this
  }

  /**
   * 상호작용 모드 설정
   * @param mode - 모드
   * @returns this
   */
  setInteractionMode(mode: boolean): DirectionBuilder {
    this.config.interactionMode = mode
    return this
  }

  /**
   * Export 데이터에서 설정 로드
   * @param data - Export 데이터
   * @returns this
   */
  fromExportData(data: CanvasEngineExportEntityType): DirectionBuilder {
    if (data.type !== SHAPES.DIRECTION) {
      throw new Error('Invalid entity type for DirectionBuilder')
    }

    // points를 상대좌표에서 절대좌표로 변환
    const absolutePoints = data.points
      ? data.points.map((p, i) => (i % 2 === 0 ? p + data.point.x : p + data.point.y))
      : undefined

    this.setId(data.shapeId)
      .setCreatePoint(data.point)
      .setBalloonPoint(data.balloonPoint)
      .setColor(data.color)
      .setLayerId(data.layerId)

    if (absolutePoints) {
      this.setPoints(absolutePoints)
    }

    if (data.balloonFontColor) this.setBalloonFontColor(data.balloonFontColor)
    if (data.balloonFillColor) this.setBalloonFillColor(data.balloonFillColor)
    if (data.balloonScale !== undefined && data.balloonScale !== null)
      this.setBalloonScale(data.balloonScale)
    if (data.connectedLineThicknessScale)
      this.setConnectedLineThicknessScale(data.connectedLineThicknessScale)
    if (data.balloonFontScale !== undefined && data.balloonFontScale !== null)
      this.setBalloonFontScale(data.balloonFontScale)
    if (data.pointShapeScale) this.setPointShapeScale(data.pointShapeScale)
    if (data.areaBorderThicknessScale)
      this.setAreaBorderThicknessScale(data.areaBorderThicknessScale)

    return this
  }

  /**
   * 설정 구성 반환
   * @returns DirectionInitConfig
   */
  build(): DirectionInitConfig {
    if (!this.config.config) {
      throw new Error('config is required')
    }

    return {
      id: this.config.id,
      createPoint: this.config.createPoint ?? { x: 0, y: 0 },
      balloonPoint: this.config.balloonPoint,
      config: this.config.config,
      points: this.config.points,
      layerId: this.config.layerId,
      balloonFontColor: this.config.balloonFontColor,
      balloonFillColor: this.config.balloonFillColor,
      balloonScale: this.config.balloonScale,
      connectedLineThicknessScale: this.config.connectedLineThicknessScale,
      balloonFontScale: this.config.balloonFontScale,
      pointShapeScale: this.config.pointShapeScale,
      areaBorderThicknessScale: this.config.areaBorderThicknessScale,
      interactionMode: this.config.interactionMode,
    }
  }

  /**
   * CanvasEngine 반환
   * @returns CanvasEngine 컨트롤러
   */
  getCanvasEngine(): ICanvasEngineEntityController {
    return this.canvasEngine
  }
}
